import {
  durationUnitsFromAbcTok,
  expandFigure,
  figureLabel,
  type RhythmFigureId,
} from './rhythmTokens';
import {
  attackHardEndMs,
  computeAdaptiveNoteWindows,
  type RhythmAttackEvent,
} from './rhythmTiming';

export interface EcoRitmoExerciseDef {
  figures: RhythmFigureId[];
  bpm?: number;
  passScore?: number;
}

export interface RhythmPatternTimeline {
  bpm: number;
  msPerUnit: number;
  totalMs: number;
  slots: Array<{
    abcTok: string;
    figureId: RhythmFigureId;
    label: string;
    isRest: boolean;
    startMs: number;
    durationMs: number;
    cellIndex: number;
  }>;
  cells: Array<{
    figureId: RhythmFigureId;
    label: string;
    isRest: boolean;
    startMs: number;
    durationMs: number;
    slotIndices: number[];
  }>;
  attacks: RhythmAttackEvent[];
}

export function buildPatternTimeline(def: EcoRitmoExerciseDef): RhythmPatternTimeline {
  const bpm = def.bpm ?? 80;
  const quarterMs = (60 / bpm) * 1000;
  const msPerUnit = quarterMs / 2;

  const slots: RhythmPatternTimeline['slots'] = [];
  const cells: RhythmPatternTimeline['cells'] = [];
  const attacks: RhythmAttackEvent[] = [];

  let cursor = 0;

  def.figures.forEach((fig, cellIndex) => {
    const atoms = expandFigure(fig);
    const cellStart = cursor;
    const slotIndices: number[] = [];

    atoms.forEach((atom) => {
      const units = durationUnitsFromAbcTok(atom.abcTok) ?? 1;
      const durationMs = units * msPerUnit;
      const slotIndex = slots.length;
      slotIndices.push(slotIndex);

      slots.push({
        abcTok: atom.abcTok,
        figureId: fig,
        label: figureLabel(fig),
        isRest: atom.isRest,
        startMs: cursor,
        durationMs,
        cellIndex,
      });

      if (!atom.isRest) {
        attacks.push({
          index: attacks.length,
          abcTok: atom.abcTok,
          startMs: cursor,
          durationMs,
          earlyMs: 0,
          lateMs: 0,
          lateGraceMs: 0,
          status: 'pending',
          timingErrorMs: null,
        });
      }

      cursor += durationMs;
    });

    cells.push({
      figureId: fig,
      label: figureLabel(fig),
      isRest: atoms.every((a) => a.isRest),
      startMs: cellStart,
      durationMs: cursor - cellStart,
      slotIndices,
    });
  });

  const sortedAttacks = [...attacks].sort((a, b) => a.startMs - b.startMs);
  for (let i = 0; i < sortedAttacks.length; i++) {
    const ev = sortedAttacks[i]!;
    const next = sortedAttacks[i + 1];
    const ioiMs = next ? next.startMs - ev.startMs : ev.durationMs;
    const windows = computeAdaptiveNoteWindows(ev.durationMs, ioiMs);
    ev.earlyMs = windows.earlyMs;
    ev.lateMs = windows.lateMs;
    ev.lateGraceMs = windows.lateGraceMs;
  }

  return { bpm, msPerUnit, totalMs: cursor, slots, cells, attacks: sortedAttacks };
}

export function summarizeAttackScore(attacks: RhythmAttackEvent[]): number {
  if (attacks.length === 0) return 0;
  let points = 0;
  for (const ev of attacks) {
    if (ev.status === 'hit') points += 100;
    else if (ev.status === 'late') points += 70;
  }
  return Math.round(points / attacks.length);
}

export function allAttacksResolved(attacks: RhythmAttackEvent[]): boolean {
  return attacks.every((a) => a.status !== 'pending');
}

export function finalizeExpiredAttacks(attacks: RhythmAttackEvent[], tMs: number): boolean {
  let changed = false;
  for (const ev of attacks) {
    if (ev.status !== 'pending') continue;
    if (tMs > attackHardEndMs(ev)) {
      ev.status = 'miss';
      changed = true;
    }
  }
  return changed;
}
